import React, { useCallback, useMemo, type FC, type ReactNode } from 'react';
import { observer } from '@legendapp/state/react';
import { useColorScheme, useTheme, Text } from '../../../../../utils';
import { giftsSelectorWidgetContentsStyles } from './gifts-selector-widget-contents.styles';
import { View, SectionList } from '../../../../../utils/react-native-stubs';
import { CurrentSelectedGiftsComponent } from '../current-selected-gifts-component';
import { RemainingGiftsSelectionComponent } from '../remaining-gifts-selection-component';
import { useProductGiftHook } from '../../../hooks/product-gift.hook';
import { useGiftSelection } from '../../../hooks/use-gift-selection.hook';
import type { GiftBottomSheetProps, GiftSection } from '../gifts-selector-widget-contents.types';
import type { ProductLike } from '../../selected-gifts/selected-gifts.types';
import { GiftContentSkeleton, Icon } from '@kidslabs/mario-nextjs-react-ui-core';

// Stub components for web using UI Core
const TextIcon: React.FC<{
    leftIcon?: React.ReactNode;
    text?: string;
    fontVariant?: string;
    textStyles?: React.CSSProperties;
    containerStyles?: React.CSSProperties;
}> = ({ leftIcon, text, textStyles, containerStyles }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, ...containerStyles }}>
        {leftIcon}
        <Text size="medium" style={textStyles}>
            {text}
        </Text>
    </div>
);
const GiftBox: React.FC<{ width?: number; height?: number }> = () => (
    <Icon
        type="data-uri"
        dataUriProps={{
            src: 'https://static.kidsplaza.vn/assets/icons/active-gift.png',
            alt: 'Giftbox icon',
        }}
        size="xlarge"
    />
);
const Divider: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
    <hr style={{ border: 'none', borderTop: '1px solid #D3D4D7', margin: '16px 0', ...style }} />
);

// Web replacement for SectionList - simple list with sections

// Memoized section header component for performance
const SectionHeader = React.memo<{
    section: string;
    selectedGift: boolean;
    styles: ReturnType<typeof giftsSelectorWidgetContentsStyles>;
}>(({ section, selectedGift, styles }) => {
    return (
        <View style={selectedGift ? styles.renderSectionHeaderSelected : styles.renderSectionHeader}>
            <TextIcon
                leftIcon={selectedGift && <GiftBox width={16} height={16} />}
                text={section}
                fontVariant={selectedGift ? 'h3' : 'body'}
                textStyles={selectedGift ? styles.titleSelectedSection : styles.titleRemainingSection}
                containerStyles={styles.sectionHeaderTitleSelected}
            />
            <Divider style={selectedGift ? styles.selectedDividerSelected : styles.selectedDivider} />
        </View>
    );
});

SectionHeader.displayName = 'SectionHeader';

const GiftsSelectorWidgetContentsInner: FC<GiftBottomSheetProps> = observer(
    ({
        product,
        productQuantity,
        cartItemReference,
        availableGiftsForRule,
        isReplacementMode = false,
        isLoading = false,
        enableAutoFill,
        onAutoFillQuantity,
    }) => {
        const theme = useTheme();
        const colorScheme = useColorScheme();
        const styles = useMemo(() => giftsSelectorWidgetContentsStyles(theme, colorScheme), [theme, colorScheme]);

        // Determine context and resolve sessionId properly:
        // - Cart context: sessionId comes from cartItemReference.logics[0].sessionId
        // - PDP context: sessionId comes from the active session in the gift selection store
        const isCartContext = !!cartItemReference;
        const cartSessionId = cartItemReference?.logics?.[0]?.sessionId ?? '';
        const pdpGiftHook = useGiftSelection();
        const pdpActiveSessionId = !isCartContext
            ? (pdpGiftHook.getActiveSessionId(`${product?.id}`, 'product') ?? '')
            : '';
        const sessionId = isCartContext ? cartSessionId : pdpActiveSessionId;

        // ✅ CRITICAL FIX: Use availableGiftsForRule if provided (for single rule change)
        // Otherwise fetch all gifts (for full gift selection)
        // Note: Hook always runs, but we'll use availableGiftsForRule when provided
        const { sortedGiftsDataList: fetchedGiftsDataList } = useProductGiftHook({
            product: product as Parameters<typeof useProductGiftHook>[0]['product'],
            observeStore: true,
        });

        // Use provided gifts or fetched gifts
        const sortedGiftsDataList = useMemo(() => {
            if (availableGiftsForRule && availableGiftsForRule.length > 0) {
                return availableGiftsForRule;
            }
            return fetchedGiftsDataList;
        }, [availableGiftsForRule, fetchedGiftsDataList]);

        // Memoize section header renderer
        const renderSectionHeader = useCallback(
            ({ section: { title, selectedGiftSection } }: { section: GiftSection }) => {
                return <SectionHeader section={title} selectedGift={selectedGiftSection} styles={styles} />;
            },
            [styles]
        );

        // Memoize section item renderer to prevent recreating on every render
        const renderSectionItem = useCallback(({ section }: { section: GiftSection }): ReactNode => {
            if (section.selectedGiftSection) {
                return <CurrentSelectedGiftsComponent giftSection={section} />;
            }

            return <RemainingGiftsSelectionComponent giftSection={section} />;
        }, []);

        const sections = useMemo<GiftSection[]>(() => {
            const baseSections: GiftSection[] = [
                {
                    title: 'Quà đã chọn',
                    selectedGiftSection: true,
                    data: [product],
                    product: product,
                    sortedGiftsDataList,
                    productQuantity,
                    cartItemReference,
                    isReplacementMode,
                    sessionId,
                    enableAutoFill,
                    onAutoFillQuantity,
                },
                {
                    title: 'Đơn hàng của bạn thỏa mãn các điều kiện tặng quà, vui lòng chọn thêm các quà tặng sau:',
                    selectedGiftSection: false,
                    data: [product],
                    product: product,
                    sortedGiftsDataList,
                    productQuantity,
                    cartItemReference,
                    isReplacementMode,
                    sessionId,
                    enableAutoFill,
                    onAutoFillQuantity,
                },
            ];

            return baseSections;
        }, [
            product,
            sortedGiftsDataList,
            productQuantity,
            cartItemReference,
            isReplacementMode,
            sessionId,
            enableAutoFill,
            onAutoFillQuantity,
        ]);

        // Memoize key extractor
        const keyExtractor = useCallback((item: ProductLike, index: number) => `${item.id ?? index}-${index}`, []);
        if (isLoading) {
            return <GiftContentSkeleton />;
        }

        return (
            <View style={styles.bottomSheetInjectContent}>
                <SectionList
                    sections={sections as unknown as Parameters<typeof SectionList<ProductLike>>[0]['sections']}
                    keyExtractor={keyExtractor}
                    renderItem={
                        renderSectionItem as unknown as Parameters<typeof SectionList<ProductLike>>[0]['renderItem']
                    }
                    renderSectionHeader={
                        renderSectionHeader as unknown as Parameters<
                            typeof SectionList<ProductLike>
                        >[0]['renderSectionHeader']
                    }
                    removeClippedSubviews={true}
                    maxToRenderPerBatch={5}
                    windowSize={10}
                    initialNumToRender={5}
                />
            </View>
        );
    }
);

GiftsSelectorWidgetContentsInner.displayName = 'GiftsSelectorWidgetContentsInner';

export const GiftsSelectorWidgetContents: FC<GiftBottomSheetProps> = observer(
    ({
        product,
        productQuantity,
        cartItemReference,
        availableGiftsForRule,
        isLoading,
        isReplacementMode,
        enableAutoFill,
        onAutoFillQuantity,
    }) => {
        return (
            <GiftsSelectorWidgetContentsInner
                product={product}
                isLoading={isLoading}
                productQuantity={productQuantity}
                cartItemReference={cartItemReference}
                availableGiftsForRule={availableGiftsForRule}
                isReplacementMode={isReplacementMode}
                enableAutoFill={enableAutoFill}
                onAutoFillQuantity={onAutoFillQuantity}
            />
        );
    }
);
